import { useParams } from "react-router";
import { useGetInspectionQuery } from "@/services/backend/inspections/service";
import { useGetDeliveryByProcessQuery } from "@/services/backend/deliveries/delivery/service";
import { useSampleSetListQuery } from "@/services/backend/sample-set/service";
import { SampleSet } from "@/services/backend/sample-set/sample-set";
import { useGetServiceObjectQuery } from "@/services/backend/customers/customer/service-object-service";
import { useShowUserQuery } from "@/services/ms-graph/ms-graph-api";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { formatDate, formatTime, isSetDate } from "@/shared/utilities/date";
import { testnorm as testnormLabels } from "@/shared/components/enum-labels";

function uniqueSorted(values: string[]): string {
  const unique = new Set(
    values
      .flatMap((value) => value.split(","))
      .map((value) => value.trim())
      .filter((value) => value !== ""),
  );
  return Array.from(unique).sort().join(", ");
}

function getSampleTakers(sets: SampleSet[]): string {
  return uniqueSorted(sets.map((set) => set.sampleTakers));
}

function getSampledBy(sets: SampleSet[]): string {
  return uniqueSorted(sets.map((set) => set.sampledBy));
}

function getSampleDate(sets: SampleSet[]): string | undefined {
  return sets[0]?.sampleDate;
}

function getTestnorm(sets: SampleSet[]): string | undefined {
  const testnorm = sets[0]?.testnorm;
  return testnorm ? testnormLabels[testnorm] : undefined;
}

function MetadataField({
  id,
  label,
  value,
}: {
  id: string;
  label: string;
  value: string;
}) {
  return (
    <div className="grid w-full items-center gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} disabled onChange={() => {}} />
    </div>
  );
}

export function MetadataIndexRoute() {
  const { processId, reportId } = useParams();

  const {
    data: inspectionData,
    isLoading: isInspectionLoading,
    error: inspectionError,
  } = useGetInspectionQuery({ id: reportId as string });
  const {
    data: delivery,
    isLoading: isDeliveryLoading,
    error: deliveryError,
  } = useGetDeliveryByProcessQuery({ processId: processId as string });
  const {
    data: sampleSets,
    isLoading: isSampleSetsLoading,
    error: sampleSetsError,
  } = useSampleSetListQuery({
    ownerId: { active: true, values: [reportId as string] },
    ownerType: { active: true, values: ["inspections.Inspection"] },
    limit: 0,
    offset: 0,
  });

  const inspection = inspectionData?.inspection;

  const { data: serviceObject, isLoading: isServiceObjectLoading } =
    useGetServiceObjectQuery(
      { id: delivery?.serviceObjectId ?? "" },
      { skip: !delivery },
    );
  const { data: createdByUser, isLoading: isCreatedByLoading } =
    useShowUserQuery(
      { id: inspection?.createdBy ?? "" },
      { skip: !inspection },
    );
  const { data: immutabilitySetByUser, isLoading: isImmutabilitySetByLoading } =
    useShowUserQuery(
      { id: inspection?.immutabilitySetBy ?? "" },
      { skip: !inspection || !inspection.immutable },
    );

  // serviceObject/createdByUser/immutabilitySetByUser errors are not fatal —
  // a missing/unresolvable lookup just falls back to "-" per field, matching
  // the old ServiceObjectName/Username components' graceful degradation.
  const error = inspectionError || deliveryError || sampleSetsError;
  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (
    isInspectionLoading ||
    isDeliveryLoading ||
    isSampleSetsLoading ||
    isServiceObjectLoading ||
    isCreatedByLoading ||
    isImmutabilitySetByLoading ||
    !inspectionData ||
    !delivery ||
    !sampleSets ||
    !inspection
  ) {
    return <Skeleton className="h-64 w-full" />;
  }

  const sets = sampleSets.items;
  const sampleDate = getSampleDate(sets);
  const testnorm = getTestnorm(sets);

  return (
    <div className="flex flex-col gap-4">
      <Card className="shadow-none">
        <CardHeader>
          <CardTitle>Proben</CardTitle>
          <CardDescription>
            Angaben zur Probenahme und -anlieferung
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <MetadataField
              id="object"
              label="Objekt"
              value={serviceObject?.name ?? "-"}
            />
            <MetadataField
              id="sampledBy"
              label="Probenahme durch"
              value={getSampledBy(sets) || "-"}
            />
            <MetadataField
              id="sampleTakers"
              label="Probennehmer*in"
              value={getSampleTakers(sets) || "-"}
            />
            <MetadataField
              id="sampleDate"
              label="Probenahme am"
              value={isSetDate(sampleDate) ? formatDate(sampleDate) : "-"}
            />
            <MetadataField
              id="testnorm"
              label="Prüfgrundlage"
              value={testnorm || "-"}
            />
            <MetadataField
              id="deliveryDate"
              label="Probeneingang"
              value={
                isSetDate(delivery.deliveryDate)
                  ? formatDate(delivery.deliveryDate)
                  : "-"
              }
            />
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-none">
        <CardHeader>
          <CardTitle>Prozess</CardTitle>
          <CardDescription>Zeitraum der Prüfung</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <MetadataField
              id="processStartedAt"
              label="Prozessstart"
              value={
                isSetDate(inspection.processStartedAt)
                  ? `${formatDate(inspection.processStartedAt as string)} ${formatTime(inspection.processStartedAt as string)}`
                  : "-"
              }
            />
            <MetadataField
              id="processEndedAt"
              label="Prozessende"
              value={
                isSetDate(inspection.processEndedAt)
                  ? `${formatDate(inspection.processEndedAt as string)} ${formatTime(inspection.processEndedAt as string)}`
                  : "-"
              }
            />
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-none">
        <CardHeader>
          <CardTitle>Erstellung & Freigabe</CardTitle>
          <CardDescription>Nachvollziehbarkeit des Berichts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <MetadataField
              id="createdAt"
              label="Erstellt am"
              value={`${formatDate(inspection.createdAt)} ${formatTime(inspection.createdAt)}`}
            />
            <MetadataField
              id="createdBy"
              label="Erstellt von"
              value={createdByUser?.displayName ?? "-"}
            />
            {inspection.immutable && (
              <>
                <MetadataField
                  id="immutabilitySetAt"
                  label="Freigegeben am"
                  value={`${formatDate(inspection.immutabilitySetAt)} ${formatTime(inspection.immutabilitySetAt)}`}
                />
                <MetadataField
                  id="immutabilitySetBy"
                  label="Freigegeben von"
                  value={immutabilitySetByUser?.displayName ?? "-"}
                />
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default MetadataIndexRoute;
